// src/components/clients/CuentasCorrientesTab.js
import React, { useState, useEffect } from 'react';
import { Table, Button, Tag, Space, DatePicker, message, Modal, Input, Tooltip, Card, Statistic, Select } from 'antd';
import { 
    EyeOutlined, 
    ReloadOutlined, 
    DollarOutlined, 
    ExclamationCircleOutlined,
    ClockCircleOutlined,
    SearchOutlined
} from '@ant-design/icons';
import { getCuentasCorrientes, registerPayment, cancelCuenta } from '../../services/cuentasCorrientesService';
import { createCreditNote } from '../../services/creditNoteService';
import dayjs from 'dayjs';

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

const { RangePicker } = DatePicker;
const { Option } = Select;

const CuentasCorrientesTab = () => {
    const [cuentas, setCuentas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
    });
    const [filters, setFilters] = useState({
        estado: null,
        dateFrom: null,
        dateTo: null,
        onlyOverdue: false,
        cliente: null,
        numero: null
    });
    const [sorting, setSorting] = useState({
        sortBy: 'fecha_emision',
        sortOrder: 'DESC'  // DESC sends to backend for newest first (backend is backwards)
    });
    const [selectedCuenta, setSelectedCuenta] = useState(null);
    const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);
    const [paymentAmount, setPaymentAmount] = useState(0);
    const [paymentMetodo, setPaymentMetodo] = useState('Efectivo');
    const [paymentReferencia, setPaymentReferencia] = useState('');
    const [summary, setSummary] = useState({
        total_saldo_pendiente: 0,
        vencido_count: 0,
        pendiente_count: 0,
        total_facturado: 0
    });

    useEffect(() => {
        fetchCuentas(pagination.current, pagination.pageSize);
    }, []);

    const fetchCuentas = async (page = 1, pageSize = 20) => {
        setLoading(true);
        try {
            const response = await getCuentasCorrientes({
                ...filters,
                ...sorting,
                page,
                pageSize
            });

            console.log('[CuentasCorrientes] API Response:', response);
            console.log('[CuentasCorrientes] Summary from API:', response?.data?.summary);
            if (response?.data?.cuentas?.[0]) {
                console.log('[CuentasCorrientes] Sample record fields:', Object.keys(response.data.cuentas[0]));
                console.log('[CuentasCorrientes] Sample record:', response.data.cuentas[0]);
            }

            if (response && response.success) {
                setCuentas(response.data.cuentas || []);
                setSummary(response.data.summary || {});
                setPagination({
                    current: response.data.pagination?.currentPage || page,
                    pageSize: response.data.pagination?.pageSize || pageSize,
                    total: response.data.pagination?.totalCount || 0,
                });
            } else {
                message.error('Error al cargar cuentas corrientes');
            }
        } catch (error) {
            console.error('Error fetching cuentas:', error);
            message.error('Error al cargar cuentas del servidor');
        } finally {
            setLoading(false);
        }
    };

    const handleApplyFilters = () => {
        fetchCuentas(1, pagination.pageSize);
    };

    const handleClearFilters = () => {
        const defaultFilters = {
            estado: null,
            dateFrom: null,
            dateTo: null,
            onlyOverdue: false,
            cliente: null,
            numero: null
        };
        const defaultSorting = {
            sortBy: 'fecha_emision',
            sortOrder: 'DESC'  // DESC to backend = newest first
        };
        setFilters(defaultFilters);
        setSorting(defaultSorting);
        // Immediately fetch with no filters and default sorting
        setLoading(true);
        getCuentasCorrientes({
            ...defaultFilters,
            ...defaultSorting,
            page: 1,
            pageSize: pagination.pageSize
        }).then(response => {
            if (response && response.success) {
                setCuentas(response.data.cuentas || []);
                setSummary(response.data.summary || {});
                setPagination({
                    current: 1,
                    pageSize: pagination.pageSize,
                    total: response.data.pagination?.totalCount || 0,
                });
            } else {
                message.error('Error al cargar cuentas corrientes');
            }
        }).catch(error => {
            console.error('Error fetching cuentas:', error);
            message.error('Error al cargar cuentas del servidor');
        }).finally(() => {
            setLoading(false);
        });
    };

    // Fetch cuentas with specific filters (used by other handlers)
    const fetchCuentasWithFilters = async (filterOverrides, page = 1, pageSize = 20) => {
        setLoading(true);
        try {
            const response = await getCuentasCorrientes({
                ...filterOverrides,
                ...sorting,
                page,
                pageSize
            });

            if (response && response.success) {
                setCuentas(response.data.cuentas || []);
                setSummary(response.data.summary || {});
                setPagination({
                    current: response.data.pagination?.currentPage || page,
                    pageSize: response.data.pagination?.pageSize || pageSize,
                    total: response.data.pagination?.totalCount || 0,
                });
            } else {
                message.error('Error al cargar cuentas corrientes');
            }
        } catch (error) {
            console.error('Error fetching cuentas:', error);
            message.error('Error al cargar cuentas del servidor');
        } finally {
            setLoading(false);
        }
    };

    const handleRegisterPayment = async () => {
        if (!selectedCuenta || paymentAmount <= 0) {
            message.error('Monto inválido');
            return;
        }

        if (paymentAmount > selectedCuenta.saldo_pendiente) {
            message.error(`El monto no puede exceder el saldo pendiente: $${Number(selectedCuenta.saldo_pendiente).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`);
            return;
        }

        try {
            const response = await registerPayment(selectedCuenta.id, {
                amount: paymentAmount,
                metodo: paymentMetodo,
                referencia: paymentReferencia
            });

            if (response && response.success) {
                message.success('Pago registrado exitosamente');
                setIsPaymentModalVisible(false);
                setPaymentAmount(0);
                setPaymentMetodo('Efectivo');
                setPaymentReferencia('');
                setSelectedCuenta(null);
                fetchCuentas(pagination.current, pagination.pageSize);
            } else {
                message.error(response?.message || 'Error al registrar pago');
            }
        } catch (error) {
            console.error('Error registering payment:', error);
            message.error(error.response?.data?.message || 'Error al registrar pago');
        }
    };

    const handleCancelCuenta = (record) => {
        // Get invoice number for display (handle multiple possible field names)
        const invoiceNumber = record.invoice_number || record.numero_factura || record.numero_comprobante || 'N/A';
        // Ensure invoice ID is a number (parseInt handles both string and number inputs)
        const invoiceId = parseInt(record.invoice_id || record.factura_id, 10);
        
        // Log the full record for debugging
        console.log('[CuentasCorrientes] Cancel record data:', record);
        console.log('[CuentasCorrientes] Invoice ID (parsed):', invoiceId, 'Type:', typeof invoiceId);
        
        Modal.confirm({
            title: '¿Cancelar Cuenta Corriente y Emitir Nota de Crédito?',
            icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    <p>Esta acción realizará lo siguiente:</p>
                    <ol style={{ paddingLeft: '20px', margin: '10px 0' }}>
                        <li><strong>Emitir una Nota de Crédito</strong> para anular la factura "{invoiceNumber}"</li>
                        <li><strong>Cancelar la cuenta corriente</strong> asociada</li>
                    </ol>
                    <p style={{ color: '#ff4d4f', marginTop: '10px' }}>
                        <strong>⚠️ Esta acción no se puede deshacer.</strong>
                    </p>
                </div>
            ),
            okText: 'Sí, emitir NC y cancelar',
            okType: 'danger',
            cancelText: 'No',
            width: 500,
            onOk: async () => {
                // Show loading message
                const hideLoading = message.loading('Generando Nota de Crédito y cancelando cuenta...', 0);
                
                try {
                    // Step 1: Create Credit Note for the invoice
                    if (!invoiceId || isNaN(invoiceId)) {
                        throw new Error('No se encontró el ID de la factura asociada');
                    }
                    
                    console.log('[CuentasCorrientes] Creating credit note for invoice ID:', invoiceId);
                    const creditNoteResponse = await createCreditNote(invoiceId, {
                        reason: 'Anulación de cuenta corriente'
                    });
                    
                    if (!creditNoteResponse || !creditNoteResponse.success) {
                        throw new Error(creditNoteResponse?.message || 'Error al generar la Nota de Crédito');
                    }
                    
                    console.log('[CuentasCorrientes] Credit note created:', creditNoteResponse);
                    
                    // Step 2: Cancel the cuenta corriente
                    const cancelResponse = await cancelCuenta(record.id, 'Cancelado - NC emitida');
                    
                    if (!cancelResponse || !cancelResponse.success) {
                        // Credit note was created but AR cancellation failed
                        message.warning('Nota de Crédito emitida, pero hubo un error al actualizar la cuenta corriente');
                    } else {
                        // Get credit note number for success message
                        const ncNumber = creditNoteResponse.data?.numeroComprobante || 
                                         creditNoteResponse.creditNote?.numero_comprobante || 
                                         'generada';
                        const stockRestoredCount = creditNoteResponse.creditNote?.stockRestoredCount || 0;
                        const stockMsg = stockRestoredCount > 0 ? ` Stock restaurado: ${stockRestoredCount} producto${stockRestoredCount > 1 ? 's' : ''}.` : '';
                        message.success(`NC ${ncNumber} emitida. CC cancelada.${stockMsg}`);
                    }
                    
                    // Refresh the list
                    fetchCuentas(pagination.current, pagination.pageSize);
                    
                } catch (error) {
                    console.error('[CuentasCorrientes] Error in cancel flow:', error);
                    message.error(`Error: ${error.message || 'No se pudo completar la operación'}`);
                } finally {
                    hideLoading();
                }
            },
        });
    };

    const getEstadoColor = (estado) => {
        const colors = {
            'PENDIENTE': 'blue',
            'VENCIDO': 'red',
            'PAGADO': 'green',
            'PARCIALMENTE_PAGADO': 'orange',
            'CANCELADO': 'default'
        };
        return colors[estado] || 'default';
    };

    const getEstadoLabel = (estado) => {
        const labels = {
            'PENDIENTE': 'Pendiente',
            'VENCIDO': 'Vencido',
            'PAGADO': 'Pagado',
            'PARCIALMENTE_PAGADO': 'Parcial',
            'CANCELADO': 'Cancelado'
        };
        return labels[estado] || estado;
    };

    const formatPrice = (price) => {
        if (typeof price !== 'number' || isNaN(price)) return 'N/A';
        return `$ ${price.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const columns = [
        {
            title: 'Nº Factura',
            dataIndex: 'invoice_number',
            key: 'invoice_number',
            width: 150,
            sorter: true,
            render: (text, record) => {
                // Support multiple possible field names from backend
                const invoiceNum = text || record.numero_factura || record.numero_comprobante || record.factura_numero || '-';
                return <strong>{invoiceNum}</strong>;
            },
        },
        {
            title: 'Cliente',
            dataIndex: 'cliente_nombre',
            key: 'cliente_nombre',
            sorter: true,
            render: (text, record) => {
                // Support multiple possible field names
                const nombre = text || record.razon_social || record.cliente_razon_social || record.nombre || 'Sin cliente';
                const documento = record.cliente_documento || record.documento_nro || record.cuit || '';
                return (
                    <div>
                        <div>{nombre}</div>
                        {documento && (
                            <div style={{ fontSize: '12px', color: '#888' }}>
                                CUIT: {documento}
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Emisión',
            dataIndex: 'fecha_factura',
            key: 'fecha_factura',
            width: 110,
            sorter: true,
            render: (date, record) => {
                // Support multiple possible field names
                const fecha = date || record.fecha_emision || record.created_at;
                if (!fecha) return 'N/A';
                // Parse date without timezone conversion
                const dateStr = String(fecha).split('T')[0]; // Get date part only
                const [year, month, day] = dateStr.split('-');
                return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
            },
        },
        {
            title: 'Vencimiento',
            dataIndex: 'fecha_vencimiento',
            key: 'fecha_vencimiento',
            width: 145,
            sorter: true,
            render: (date, record) => {
                if (!date) return 'N/A';
                // Parse date without timezone conversion
                const dateStr = String(date).split('T')[0]; // Get date part only
                const [year, month, day] = dateStr.split('-');
                const formattedDate = `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
                
                const isOverdue = record.dias_vencido > 0;
                return (
                    <div style={{ color: isOverdue ? '#ff4d4f' : 'inherit', fontWeight: isOverdue ? 'bold' : 'normal' }}>
                        {formattedDate}
                        {isOverdue && (
                            <div style={{ fontSize: '11px' }}>
                                ({record.dias_vencido} días)
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Total',
            dataIndex: 'monto_total',
            key: 'monto_total',
            width: 120,
            align: 'right',
            sorter: true,
            render: (total, record) => {
                // Support multiple possible field names
                const monto = total || record.total || record.importe_total || 0;
                return formatPrice(Number(monto));
            },
        },
        {
            title: 'Pagado',
            dataIndex: 'monto_pagado',
            key: 'monto_pagado',
            width: 120,
            align: 'right',
            sorter: true,
            render: (pagado) => (
                <span style={{ color: '#52c41a' }}>
                    {formatPrice(pagado || 0)}
                </span>
            ),
        },
        {
            title: 'Saldo',
            dataIndex: 'saldo_pendiente',
            key: 'saldo_pendiente',
            width: 120,
            align: 'right',
            sorter: true,
            render: (saldo, record) => {
                const isPaid = record.estado === 'PAGADO';
                return (
                    <span style={{ 
                        color: isPaid ? '#52c41a' : (saldo > 0 ? '#ff4d4f' : 'inherit'), 
                        fontWeight: 'bold' 
                    }}>
                        {formatPrice(saldo)}
                    </span>
                );
            },
        },
        {
            title: 'Estado',
            dataIndex: 'estado',
            key: 'estado',
            width: 120,
            sorter: true,
            render: (estado) => (
                <Tag color={getEstadoColor(estado)}>
                    {getEstadoLabel(estado)}
                </Tag>
            ),
        },
        {
            title: 'Acciones',
            key: 'actions',
            width: 150,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    {(record.estado === 'PENDIENTE' || record.estado === 'VENCIDO' || record.estado === 'PARCIALMENTE_PAGADO') && (
                        <Tooltip title="Registrar Cobro">
                            <Button
                                type="primary"
                                size="small"
                                icon={<DollarOutlined />}
                                onClick={() => {
                                    if (isDemoMode) { message.info('Registro de cobro sobre cuenta corriente — actualiza el saldo pendiente del cliente'); return; }
                                    setSelectedCuenta(record);
                                    setPaymentAmount(Number(record.saldo_pendiente));
                                    setPaymentMetodo('Efectivo');
                                    setPaymentReferencia('');
                                    setIsPaymentModalVisible(true);
                                }}
                            />
                        </Tooltip>
                    )}
                    <Tooltip title="Ver Factura">
                        <Button
                            type="default"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => {
                                if (isDemoMode) { message.info('Factura asociada a esta cuenta corriente con detalle de ítems y condiciones de pago'); return; }
                                window.open(`#/print-quote?invoiceId=${record.invoice_id}`, '_blank');
                            }}
                        />
                    </Tooltip>
                    {record.estado !== 'PAGADO' && record.estado !== 'CANCELADO' && (
                        <Tooltip title="Emitir NC y Cancelar">
                            <Button
                                danger
                                size="small"
                                icon={<ExclamationCircleOutlined />}
                                onClick={() => {
                                    if (isDemoMode) { message.warning('Emisión de Nota de Crédito y cancelación de la cuenta corriente'); return; }
                                    handleCancelCuenta(record);
                                }}
                            />
                        </Tooltip>
                    )}
                </Space>
            ),
        },
    ];

    // Use global API summary statistics (consistent across all pages and sorting)
    // Note: API provides total_saldo_pendiente (all unpaid) but doesn't break down by estado
    // If you need separate vencido balance, request backend to add 'vencido_saldo' to summary
    const totalPendiente = summary.total_saldo_pendiente || 0;
    const vencidoCount = summary.vencido_count || 0;
    const pendingCount = (summary.pendiente_count || 0) + (summary.vencido_count || 0);

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={() => fetchCuentas(pagination.current, pagination.pageSize)}
                >
                    Actualizar
                </Button>
            </div>

            {/* Summary Cards */}
            <div style={{ marginBottom: 24, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <Card size="small" style={{ borderLeft: '4px solid #cf1322', flex: '1', minWidth: '200px' }}>
                    <Statistic
                        title={<span style={{ fontSize: '16px' }}>SALDO TOTAL PENDIENTE</span>}
                        value={totalPendiente}
                        precision={2}
                        prefix="$"
                        valueStyle={{ color: totalPendiente > 0 ? '#cf1322' : '#52c41a', fontSize: '24px' }}
                    />
                </Card>
                <Card size="small" style={{ borderLeft: '4px solid #ff4d4f', flex: '1', minWidth: '200px' }}>
                    <Statistic
                        title={<span style={{ fontSize: '16px' }}>FACTURAS VENCIDAS</span>}
                        value={vencidoCount}
                        valueStyle={{ color: '#ff4d4f', fontSize: '24px' }}
                        suffix={<ExclamationCircleOutlined />}
                    />
                </Card>
                <Card size="small" style={{ borderLeft: '4px solid #faad14', flex: '1', minWidth: '200px' }}>
                    <Statistic
                        title={<span style={{ fontSize: '16px' }}>FACTURAS PENDIENTES + VENCIDAS</span>}
                        value={pendingCount}
                        valueStyle={{ color: '#faad14', fontSize: '24px' }}
                        suffix={<ClockCircleOutlined />}
                    />
                </Card>
            </div>

            {/* Filters */}
            <div style={{ marginBottom: 16, background: 'white', padding: 16, borderRadius: 8 }}>
                <Space wrap>
                    <Input
                        placeholder="Buscar cliente"
                        style={{ width: 200 }}
                        disabled={isDemoMode}
                        allowClear
                        value={filters.cliente}
                        onChange={(e) => setFilters({ ...filters, cliente: e.target.value || null })}
                        onPressEnter={handleApplyFilters}
                        prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                        title="Busca por nombre o razón social del cliente"
                    />

                    <Input
                        placeholder="Buscar x Factura (ej: 00005-00003743)"
                        style={{ width: 220 }}
                        disabled={isDemoMode}
                        allowClear
                        value={filters.numero}
                        onChange={(e) => {
                            const value = e.target.value || null;
                            // Extract only last 5 digits for search, but allow any input
                            const sanitized = value ? value.replace(/[^0-9]/g, '').slice(-5) : null;
                            setFilters({ ...filters, numero: sanitized });
                        }}
                        onPressEnter={handleApplyFilters}
                        prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                        title="Ingresa el número de factura completo o últimos dígitos (busca por últimos 5 dígitos)"
                    />

                    <Select
                        placeholder="Filtrar por estado"
                        style={{ width: 180 }}
                        disabled={isDemoMode}
                        allowClear
                        value={filters.estado}
                        onChange={(value) => {
                            setFilters({ ...filters, estado: value });
                            // Auto-apply filter on selection
                            setTimeout(() => handleApplyFilters(), 100);
                        }}
                    >
                        <Option value="PENDIENTE">Pendiente</Option>
                        <Option value="VENCIDO">Vencido</Option>
                        <Option value="PAGADO">Pagado</Option>
                        <Option value="PARCIALMENTE_PAGADO">Parcial</Option>
                        <Option value="CANCELADO">Cancelado</Option>
                    </Select>

                    <RangePicker
                        format="DD/MM/YYYY"
                        placeholder={['Desde', 'Hasta']}
                        disabled={isDemoMode}
                        onChange={(dates) => {
                            setFilters({
                                ...filters,
                                dateFrom: dates ? dates[0].format('YYYY-MM-DD') : null,
                                dateTo: dates ? dates[1].format('YYYY-MM-DD') : null,
                            });
                            // Auto-apply filter when date range is selected or cleared
                            setTimeout(() => handleApplyFilters(), 100);
                        }}
                    />
                    
                    <Button
                        type={filters.onlyOverdue ? 'primary' : 'default'}
                        danger={filters.onlyOverdue}
                        disabled={isDemoMode}
                        onClick={() => {
                            const newOverdue = !filters.onlyOverdue;
                            setFilters({ ...filters, onlyOverdue: newOverdue });
                            // Auto-apply when toggling overdue filter
                            setTimeout(() => handleApplyFilters(), 100);
                        }}
                    >
                        {filters.onlyOverdue ? 'Mostrando Vencidos' : 'Mostrar Solo Vencidos'}
                    </Button>

                    <Button type="primary" disabled={isDemoMode} onClick={handleApplyFilters}>
                        Aplicar Filtros
                    </Button>
                    
                    <Button disabled={isDemoMode} onClick={handleClearFilters}>
                        Limpiar
                    </Button>
                </Space>
            </div>

            {/* Table */}
            <Table
                columns={columns}
                dataSource={cuentas}
                loading={loading}
                rowKey="id"
                locale={{
                    triggerDesc: 'Clic para ordenar descendente',
                    triggerAsc: 'Clic para ordenar ascendente',
                    cancelSort: 'Clic para cancelar ordenamiento'
                }}
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} cuentas corrientes`,
                    onChange: (page, pageSize) => {
                        fetchCuentas(page, pageSize);
                    },
                }}
                onChange={(paginationParam, filtersParam, sorter) => {
                    // Handle sorting changes
                    if (sorter && sorter.field) {
                        const sortFieldMap = {
                            'invoice_number': 'numero',
                            'cliente_nombre': 'cliente',
                            'fecha_factura': 'fecha_emision',
                            'fecha_vencimiento': 'fecha_vencimiento',
                            'monto_total': 'total',
                            'monto_pagado': 'monto_pagado',
                            'saldo_pendiente': 'saldo_pendiente',
                            'estado': 'estado'
                        };
                        
                        const sortBy = sortFieldMap[sorter.field] || sorter.field;
                        // Backend interprets ASC/DESC backwards: flip them here
                        const sortOrder = sorter.order === 'ascend' ? 'DESC' : sorter.order === 'descend' ? 'ASC' : 'DESC';
                        
                        setSorting({ sortBy, sortOrder });
                        
                        // Fetch with new sorting
                        setLoading(true);
                        getCuentasCorrientes({
                            ...filters,
                            sortBy,
                            sortOrder,
                            page: 1, // Reset to first page on sort change
                            pageSize: paginationParam.pageSize || 20
                        }).then(response => {
                            if (response && response.success) {
                                setCuentas(response.data.cuentas || []);
                                setSummary(response.data.summary || {});
                                setPagination({
                                    current: 1,
                                    pageSize: paginationParam.pageSize || 20,
                                    total: response.data.pagination?.totalCount || 0,
                                });
                            }
                        }).catch(error => {
                            console.error('Error fetching cuentas:', error);
                            message.error('Error al cargar cuentas del servidor');
                        }).finally(() => {
                            setLoading(false);
                        });
                    }
                }}
                scroll={{ x: 1200 }}
                size="middle"
            />

            {/* Payment Modal */}
            <Modal
                title={
                    <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                        <DollarOutlined style={{ marginRight: '8px' }} />
                        Registrar Cobro
                    </div>
                }
                open={isPaymentModalVisible}
                onOk={handleRegisterPayment}
                onCancel={() => {
                    setIsPaymentModalVisible(false);
                    setPaymentAmount(0);
                    setPaymentMetodo('Efectivo');
                    setPaymentReferencia('');
                    setSelectedCuenta(null);
                }}
                okText="Registrar Cobro"
                cancelText="Cancelar"
                width={500}
            >
                {selectedCuenta && (
                    <>
                        <div style={{ marginBottom: 16, padding: 16, background: '#f5f5f5', borderRadius: 4 }}>
                            <p style={{ margin: 0, marginBottom: 8 }}>
                                <strong>Factura:</strong> {selectedCuenta.invoice_number || selectedCuenta.numero_factura || selectedCuenta.numero_comprobante || '-'}
                            </p>
                            <p style={{ margin: 0, marginBottom: 8 }}>
                                <strong>Cliente:</strong> {selectedCuenta.cliente_nombre || selectedCuenta.razon_social || selectedCuenta.cliente_razon_social || 'Sin cliente'}
                            </p>
                            <p style={{ margin: 0, marginBottom: 8 }}>
                                <strong>Total Factura:</strong> {formatPrice(Number(selectedCuenta.monto_total || selectedCuenta.total || 0))}
                            </p>
                            <p style={{ margin: 0 }}>
                                <strong>Saldo Pendiente:</strong>{' '}
                                <span style={{ color: '#ff4d4f', fontSize: '16px', fontWeight: 'bold' }}>
                                    {formatPrice(selectedCuenta.saldo_pendiente)}
                                </span>
                            </p>
                        </div>
                        
                        <div style={{ marginTop: 16 }}>
                            <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
                                Monto a Cobrar:
                            </label>
                            <Input
                                type="number"
                                value={paymentAmount}
                                onChange={(e) => setPaymentAmount(parseFloat(e.target.value) || 0)}
                                prefix="$"
                                style={{ fontSize: '16px' }}
                                max={selectedCuenta.saldo_pendiente}
                            />
                            <div style={{ marginTop: 8, fontSize: '12px', color: '#888' }}>
                                Máximo: {formatPrice(selectedCuenta.saldo_pendiente)}
                            </div>
                        </div>

                        <div style={{ marginTop: 16 }}>
                            <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
                                Método de Pago:
                            </label>
                            <Select
                                value={paymentMetodo}
                                onChange={(value) => setPaymentMetodo(value)}
                                style={{ width: '100%' }}
                            >
                                <Option value="Efectivo">Efectivo</Option>
                                <Option value="Transferencia Bancaria">Transferencia Bancaria</Option>
                                <Option value="Cheque">Cheque</Option>
                                <Option value="Débito">Débito</Option>
                                <Option value="Crédito">Crédito</Option>
                                <Option value="MercadoPago">MercadoPago</Option>
                            </Select>
                        </div>
                        
                        <div style={{ marginTop: 16 }}>
                            <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
                                Referencia de Pago (opcional):
                            </label>
                            <Input
                                value={paymentReferencia}
                                onChange={(e) => setPaymentReferencia(e.target.value)}
                                placeholder="Ej: Nº de transferencia, cheque, etc."
                            />
                        </div>
                    </>
                )}
            </Modal>
        </div>
    );
};

export default CuentasCorrientesTab;
